import axios from 'axios'
import qs from 'qs'
import { ENDPOINTS } from '@/config/endpoints'
import { getEverflowClickError, getEverflowDebugLog, getEverflowSdkError } from '@/services/everflowService'
import { parseArrayDebtResponse } from '@/utils/formatters'

// Extract CRS attribute value from passedResults by attribute code
// Returns the value range as "lower-upper" string, or single value if not a range
function extractCrsValue(passedResults, attributeCode) {
  if (!passedResults || !Array.isArray(passedResults)) return null

  const result = passedResults.find(r => r.attributeCode === attributeCode)
  if (!result || !result.value) return null

  // Value is typically [lower, upper] - return as "lower-upper" string
  if (Array.isArray(result.value)) {
    if (result.value.length >= 2) {
      return `${result.value[0]}-${result.value[1]}`
    }
    if (result.value.length === 1) {
      return String(result.value[0])
    }
    return null
  }

  // If single value (not array), return as string
  return String(result.value)
}

// Extract all relevant CRS fields for Leadspedia
export function extractCrsFields(crsData) {
  if (!crsData || !crsData.ruleEvaluation?.passedResults) return null

  const passedResults = crsData.ruleEvaluation.passedResults

  return {
    // ECM01: Credit Score
    crs_credit_score: extractCrsValue(passedResults, 'ECM01'),
    // ECM04: Delinquent Accounts
    crs_delinquent_accounts: extractCrsValue(passedResults, 'ECM04'),
    // ECM05: Derogatory Accounts
    crs_derogatory_accounts: extractCrsValue(passedResults, 'ECM05'),
    // ECM07: Open Revolving Accounts
    crs_open_revolving_accounts: extractCrsValue(passedResults, 'ECM07'),
    // ECM12: Unsecured Credit Card Balance
    crs_credit_card_balance: extractCrsValue(passedResults, 'ECM12'),
    // ECM13: Unsecured Debt Balance
    crs_unsecured_debt: extractCrsValue(passedResults, 'ECM13'),
    // ECM14: Medical Type Balance
    crs_medical_balance: extractCrsValue(passedResults, 'ECM14'),
    // ECM17: Delinquencies Past 12 Months
    crs_delinquencies_past_12mo: extractCrsValue(passedResults, 'ECM17')
  }
}

// Build form payload for Leadspedia
function buildFormPayload(dataObj) {
  // Clone and clean the data object
  const clean = { ...dataObj }

  // Remove any undefined or null values
  Object.keys(clean).forEach(key => {
    if (clean[key] === undefined || clean[key] === null) {
      delete clean[key]
    }
  })

  return clean
}

// Post lead to Leadspedia
export async function postLead(dataObj, attemptNumber) {
  const clean = buildFormPayload(dataObj)
  const postData = qs.stringify(clean)

  console.log('Lead post attempt:', attemptNumber, {
    lp_campaign_id: clean.lp_campaign_id,
    aff_id: clean.aff_id,
    array_credit: clean.array_credit,
    verified_debt: clean.verified_debt,
    // CRS fields (if present)
    crs_credit_score: clean.crs_credit_score,
    crs_delinquent_accounts: clean.crs_delinquent_accounts,
    crs_derogatory_accounts: clean.crs_derogatory_accounts,
    crs_open_revolving_accounts: clean.crs_open_revolving_accounts,
    crs_credit_card_balance: clean.crs_credit_card_balance,
    crs_unsecured_debt: clean.crs_unsecured_debt,
    crs_medical_balance: clean.crs_medical_balance,
    crs_delinquencies_past_12mo: clean.crs_delinquencies_past_12mo,
    credit_pull_provider: clean.credit_pull_provider,
    credit_pull_mode: clean.credit_pull_mode
  })

  const resp = await axios({
    method: 'post',
    maxBodyLength: Infinity,
    url: ENDPOINTS.LEAD_POST,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    data: postData,
  })

  console.log('Lead post response:', {
    attempt: attemptNumber,
    status: resp?.status,
    result: resp?.data?.result,
    msg: resp?.data?.msg,
    price: resp?.data?.price,
    lead_id: resp?.data?.lead_id
  })

  return resp
}

// Build base lead data object from form and session data
export function buildLeadData({
  questionState,
  sessionStore,
  campaignKeys,
  arrayData,
  phoneData,
  jornayaToken,
  trustedFormToken,
  showArrayDisclaimer,
  shortForm = false,
  crsData = null,
  serverVerifiedDebt = null,
  forceCRS = false,
  crsFallback = null,
  creditPullStrategy = null
}) {
  const zipCode = questionState.address.zip || sessionStore.backupZip || undefined
  const city = questionState.address.city || sessionStore.backupCity || undefined
  const state = questionState.address.state || sessionStore.backupState || undefined

  // Use server-calculated verified_debt if available, otherwise parse client-side
  let verified_debt
  if (shortForm) {
    verified_debt = undefined
  } else if (serverVerifiedDebt !== null && serverVerifiedDebt !== undefined) {
    verified_debt = serverVerifiedDebt
  } else {
    const parsed = parseArrayDebtResponse(arrayData.total_unsecured_debt)
    verified_debt = parsed.verified_debt
  }

  // Build base lead data
  const efLog = campaignKeys.everflow_aff_id
    ? getEverflowDebugLog({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    : ''
  const efSdkError = campaignKeys.everflow_aff_id
    ? getEverflowSdkError({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    : ''
  const efClickError = campaignKeys.everflow_aff_id
    ? getEverflowClickError({
      affId: campaignKeys.everflow_aff_id,
      offerId: campaignKeys.everflow_offer_id || 320
    })
    : ''
  const ryanTestPostEnabled = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('ryan_test_post') === '1'

  const leadData = {
    // Required Leadspedia fields
    lp_campaign_id: campaignKeys.lp_campaign_id,
    lp_campaign_key: campaignKeys.lp_campaign_key,
    lp_response: 'json',
    pd_company_id: campaignKeys.pd_company_id || '',
    // Contact info
    address: shortForm ? undefined : questionState.address.address || undefined,
    zip_code: zipCode,
    city,
    state,
    email_address: questionState.email.value,
    first_name: questionState.name.first,
    last_name: questionState.name.last,
    phone_home: phoneData.phone.replace(/\D/g, ''),
    dob: shortForm ? undefined : `${questionState.dob.month}/${questionState.dob.day}/${questionState.dob.year}`,
    // Form answers
    income: questionState.income.value,
    employed: shortForm ? undefined : questionState.employed.value,
    debt_amount: questionState.amountNumberValue,
    // Session/tracking data
    ip_address: sessionStore.ipAddress,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    landing_page: 'https://firstadvantage.io/app/',
    aff_id: sessionStore.affiliateId,
    lp_s1: sessionStore.formData.lp_s1 || '',
    lp_s2: sessionStore.formData.lp_s2 || '',
    lp_s3: sessionStore.formData.lp_s3 || '',
    lp_s4: sessionStore.formData.lp_s4 || '',
    lp_s5: sessionStore.formData.lp_s5 || '',
    ef_log: efLog,
    ef_sdk_error: efSdkError,
    ef_click_error: efClickError,
    // Verification tokens
    jornaya_lead_id: jornayaToken,
    trusted_form_id: trustedFormToken,
    // Array data
    array_collections: shortForm ? undefined : arrayData.collections,
    array_credit: shortForm ? undefined : arrayData.credit_score,
    array_delinquencies: shortForm ? undefined : arrayData.delinquencies,
    array_debt: shortForm ? undefined : arrayData.total_unsecured_debt,
    array_tax_liens: shortForm ? undefined : arrayData.tax_liens,
    array_student_loans: shortForm ? undefined : arrayData.open_student_loan_bal,
    verified_debt,
    // Variant/consent tracking
    short_form: shortForm ? 'Yes' : undefined,
    ryan_test_post: ryanTestPostEnabled ? 'Yes' : undefined,
    split_test_variant: sessionStore.variantId,
    adr_fcra_disclosure: shortForm ? undefined : (showArrayDisclaimer ? 'Yes' : 'No'),
    tcpaLanguage: phoneData.tcpaLanguage || '',
    validationStatus: phoneData.validationStatus || 'verified',
    credit_pull_provider: shortForm ? undefined : creditPullStrategy?.operationalProvider,
    credit_pull_mode: shortForm ? undefined : creditPullStrategy?.mode,
    credit_pull_primary: shortForm ? undefined : creditPullStrategy?.primary,
    credit_pull_fallback: shortForm ? undefined : creditPullStrategy?.fallback,
    credit_pull_fallback_used: shortForm || !creditPullStrategy ? undefined : (creditPullStrategy.fallbackUsed ? 'Yes' : 'No'),
    // Quality annotation fields (cross-funnel attribution program — FA-next
    // spec 2026-08-23): lets buyer quality slice by origin funnel and credit
    // provider across all platforms. Not mapped into buyer deliveries.
    funnel_platform: 'fa-legacy',
    funnel_skin: 'default',
    twofactorauth: 'false',
    otp_outcome: 'off',
    emp_mode: 'false',
    credit_provider: shortForm ? 'off' : (creditPullStrategy?.primary || 'off'),
    credit_provider_actual: shortForm ? 'none' : (creditPullStrategy?.operationalProvider || 'none')
  }

  // Add CRS-specific fields if CRS data is available
  const crsFields = shortForm ? null : extractCrsFields(crsData)
  if (crsFields) {
    Object.entries(crsFields).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        leadData[key] = value
      }
    })
  }

  // Add test flag when CRS is forced via URL param
  if (forceCRS) {
    // leadData.lp_test = 1
  }

  // Add crsFallback when CRS was used as fallback after Array no-hit
  if (!shortForm && crsFallback) {
    leadData.crsFallback = crsFallback
  }

  return leadData
}

// Check if lead response is a duplicate
export function isDuplicateLead(response) {
  const msg = response?.data?.msg

  if (msg === 'Lead Rejected' && Array.isArray(response?.data?.errors)) {
    return response.data.errors.some((e) =>
      typeof e?.error === 'string' && e.error.toLowerCase().includes('duplicate')
    )
  }

  return false
}

// Get advertiser ID from response clients
export function getAdvertiserFromClients(clients) {
  if (!clients || !Array.isArray(clients)) return null

  for (let i = 0; i < clients.length; i++) {
    if (clients[i]?.client?.success === 'true') {
      return String(clients[i].client.advertiserID)
    }
  }

  return null
}

// Process lead submission
export async function processLead({
  dataObj,
  attemptNumber,
  formStore,
  onAttemptIncrement
}) {
  // Post the lead
  const response = await postLead(dataObj, attemptNumber)
  const duplicate = isDuplicateLead(response)

  // Check for duplicate
  if (duplicate) {
    console.warn('Lead rejected due to duplicate:', dataObj.email_address)
    return { response, dataObj, duplicate: true }
  }

  return { response, dataObj, duplicate: false }
}

export const leadService = {
  postLead,
  buildLeadData,
  extractCrsFields,
  isDuplicateLead,
  getAdvertiserFromClients,
  processLead
}
